import axios from 'axios';
// 🔥 Global DepoTrace window reference
let depoWindow = null;

export const setDepoWindow = (windowRef) => {
  depoWindow = windowRef;
};

export const getDepoWindow = () => {
  return depoWindow;
};

// Create Axios instance
const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api/webmanager`,
  withCredentials: true, // Required for Sanctum CSRF & session
});

// === INTERCEPTORS ===
api.interceptors.request.use(
  async (config) => {
    // 1. GET SANCTUM TOKEN FROM LOCALSTORAGE
    const token = localStorage.getItem('sanctum_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // 2. SET X-XSRF-TOKEN FROM COOKIE
    const xsrfToken = document.cookie
      .split('; ')
      .find(row => row.startsWith('XSRF-TOKEN='))
      ?.split('=')[1];
    if (xsrfToken) {
      config.headers['X-XSRF-TOKEN'] = decodeURIComponent(xsrfToken);
    }

    // 3. CONTENT-TYPE
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    } else if (config.method === 'post' || config.method === 'put') {
      config.headers['Content-Type'] = 'application/json';
    }

    console.log('API Request:', {
      method: config.method.toUpperCase(),
      url: config.url,
      headers: config.headers,
      data: config.data instanceof FormData ? 'FormData' : config.data,
    });

    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    console.log('API Response:', response.status, response.data);
    return response;
  },
  (error) => {
    console.error('API Response Error:', {
      status: error.response?.status,
      data: error.response?.data,
      message: error.message,
    });

    // Auto logout on 401
    if (error.response?.status === 401) {
      localStorage.removeItem('sanctum_token');
      localStorage.removeItem('user');
      localStorage.removeItem('csrf_fetched');
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

// === AUTHENTICATION ===
export const login = async (data) => {
  const res = await api.post('/login', data);
  const { token, user } = res.data;

  localStorage.setItem('sanctum_token', token);
  localStorage.setItem('user', JSON.stringify(user));

  return res;
};

export const register = (data) => api.post('/register', data);
export const resetPassword = (data) => api.post('/reset-password', data);
export const logout = async () => {
  try {
    await api.post('/logout');
  } finally {
    localStorage.removeItem('sanctum_token');
    localStorage.removeItem('user');
    localStorage.removeItem('csrf_fetched');
  }
};

export const ensureCsrfToken = async () => true;

// 🔥 FIXED: getCsrfToken - Use RAW axios (bypass baseURL)
export const getCsrfToken = async () => true;

// === USER & ADMIN ===
export const getUsers = () => api.get('/users');
export const getResellers = () => api.get('/resellers');
export const validateUsername = (data) => api.post('/validate-username', data);
export const updateProfile = (data) => api.post('/update-profile', data);

// === ADMIN ROUTES ===
export const getAdmins = () => api.get('/admins');
export const createAdmin = (data) => api.post('/admins', data);
export const getAdmin = (id) => api.get(`/admins/${id}`);
export const updateAdmin = (id, data) => api.put(`/admins/${id}`, data);
export const updateAdminStatus = (id, data) => api.put(`/admins/${id}/status`, data);
export const checkEmail = (data) => api.post('/check-email', data);
export const checkUsername = (data) => api.post('/check-username', data);

// === CLIENT ADMINS ===
export const createClientAdmin = (data) => api.post('/client-admins', data);
export const getClientAdmins = () => api.get('/client-admins');
export const getClientAdmin = (id) => api.get(`/client-admins/${id}`);
export const updateClientAdmin = (id, data) => api.put(`/client-admins/${id}`, data);
export const deleteClientAdmin = (id) => api.delete(`/client-admins/${id}`);

// === HIERARCHY ===
export const getClientAdminsByReseller = (resellerId) => api.get(`/resellers/${resellerId}/client-admins`);
export const getClientUsersByClientAdmin = (clientAdminId) => api.get(`/client-admins/${clientAdminId}/client-users`);

// === CLIENT USERS ===
export const getClientUsersList = () => api.get('/client-users');
export const createClientUser = (data) => api.post('/client-users', data);
export const getClientUser = (id) => api.get(`/client-users/${id}`);
export const updateClientUser = (id, data) => api.put(`/client-users/${id}`, data);
export const deleteClientUser = (id) => api.delete(`/client-users/${id}`);
export const getClientUsersByAdmin = () => api.get('/client-users/by-admin');
export const getCombinedUsersForIntroducing = () => api.get('/client-users/combined-for-introducing');

export const sendSessionInvite = (data) => api.post('/session/send-invite', data);
export const shareSessionDocuments = (data) => api.post('/session/share-documents', data);

// === CASE MANAGEMENT ===
export const createCase = (data) => api.post('/cases', data);
export const getCases = () => api.get('/cases');
export const getCase = (id) => api.get(`/cases/${id}`);
export const updateCase = (id, data) => api.put(`/cases/${id}`, data);
export const deleteCase = (id) => api.delete(`/cases/${id}`);
export const getCasesClients = () => api.get('/cases/get-clients');
export const getCaseManagers = () => api.get('/case-managers');

// === 🔥 CUSTOM SESSIONS - NEW! ===
export const createCustomSession = (caseId, data) => api.post(`/cases/${caseId}/custom-sessions`, data);
export const getCustomSessionsByCase = (caseId) => api.get(`/cases/${caseId}/custom-sessions`);
export const getCustomSession = (id) => api.get(`/custom-sessions/${id}`);
export const updateCustomSession = (caseId, sessionId, data) => api.put(`/cases/${caseId}/custom-sessions/${sessionId}`, data);
export const deleteCustomSession = (caseId, sessionId) => api.delete(`/cases/${caseId}/custom-sessions/${sessionId}`);
// === ARBITRATION ===
export const createArbitration = (data) => api.post('/arbitrations', data);
export const getArbitration = (id) => api.get(`/arbitrations/${id}`);
export const updateArbitration = (id, data) => api.put(`/arbitrations/${id}`, data);
export const deleteArbitration = (id) => api.delete(`/arbitrations/${id}`);
export const getArbitrationsByCase = (caseId) => api.get(`/cases/${caseId}/arbitrations`);

// === DEPOSITION ===
export const createDeposition = (data) => api.post('/deposition-sessions', data);
export const getDeposition = (id) => api.get(`/depositions/${id}`);
export const updateDeposition = (id, data) => api.put(`/deposition-sessions/${id}`, data);
export const deleteDeposition = (id) => api.delete(`/deposition-sessions/${id}`);
export const getDepositionsByCase = (caseId) => api.get(`/cases/${caseId}/depositions`);

// === TRIAL ===
export const createTrial = (data) => api.post('/trials', data);
export const getTrial = (id) => api.get(`/trials/${id}`);
export const updateTrial = (id, data) => api.put(`/trials/${id}`, data);
export const deleteTrial = (id) => api.delete(`/trials/${id}`);
export const getTrialsByCase = (caseId) => api.get(`/cases/${caseId}/trials`);

// === MEDIATION ===
export const createMediation = (data) => api.post('/mediations', data);
export const getMediation = (id) => api.get(`/mediations/${id}`);
export const updateMediation = (id, data) => api.put(`/mediations/${id}`, data);
export const deleteMediation = (id) => api.delete(`/mediations/${id}`);
export const getMediationsByCase = (caseId) => api.get(`/cases/${caseId}/mediations`);

// === WITNESS PREP ===
export const createWitnessPrep = (data) => api.post('/witness-preps', data);
export const getWitnessPrep = (id) => api.get(`/witness-preps/${id}`);
export const updateWitnessPrep = (id, data) => api.put(`/witness-preps/${id}`, data);
export const deleteWitnessPrep = (id) => api.delete(`/witness-preps/${id}`);
export const getWitnessPrepsByCase = (caseId) => api.get(`/cases/${caseId}/witness-preps`);

// === ENTERPRISE CLIENTS ===
export const getEnterpriseClients = () => api.get('/enterprise-clients');
export const createEnterpriseClient = (data) => api.post('/enterprise-clients', data);
export const getEnterpriseClient = (id) => api.get(`/enterprise-clients/${id}`);
export const updateEnterpriseClient = (id, data) => api.put(`/enterprise-clients/${id}`, data);
export const deleteEnterpriseClient = (id) => api.delete(`/enterprise-clients/${id}`);

// === VALIDATION ===
export const checkPhone = (data) => api.post('/check-phone', data);

// === FOLDERS ===
export const getCaseFolders = (caseId, sessionId) =>
  api.get(`/cases/${caseId}/folders`, {
    params: { session_id: sessionId },
  });
export const createCaseFolder = (caseId, data) => api.post(`/cases/${caseId}/folders`, data);
export const createDefaultFolder = (caseId) => api.post(`/cases/${caseId}/folders/default`);
export const updateCaseFolder = (id, data) => api.put(`/folders/${id}`, data);
export const deleteCaseFolder = (id) => api.delete(`/folders/${id}`);
export const moveCaseFolder = (id, data) => api.post(`/folders/${id}/move`, data);
export const copyCaseFolder = (id, data) => api.post(`/folders/${id}/copy`, data);

// === DOCUMENTS ===
export const getFolderDocuments = (folderId) => api.get(`/folders/${folderId}/documents`);
export const uploadDocuments = (folderId, formData) => {
  console.log('Uploading to folder:', folderId);
  for (let pair of formData.entries()) {
    console.log(pair[0], pair[1]);
  }
  return api.post(`/folders/${folderId}/documents`, formData);
};

// === SESSION CONTROL ===
export const startSession = (id) => api.post(`/depositions/${id}/start`);
export const finishSession = (id) => api.post(`/depositions/${id}/finish`);
export const getSessionParticipants = (id) => api.get(`/depositions/${id}/participants`);
export const getSessionStatus = (id) => api.get(`/depositions/${id}/status`);
export const joinSession = (data) => api.post('/webapp/join', data);
export const setSessionDocument = (sessionId, data) =>
  api.post(`/depositions/${sessionId}/open-document`, data);

// === DOCUMENT ACTIONS ===
export const createPlaceholder = (folderId, data) => api.post(`/folders/${folderId}/placeholders`, data);
export const updateDocument = (id, data) => api.put(`/documents/${id}`, data);
export const updatePlaceholder = (id, data) => api.put(`/placeholders/${id}`, data);
export const deleteDocument = (id) => api.delete(`/documents/${id}`);
export const moveDocument = (id, data) => api.post(`/documents/${id}/move`, data);
export const viewDocument = (id) => api.get(`/documents/${id}/view`);
export const downloadDocument = (id) => api.get(`/documents/${id}/download`, { responseType: 'blob' });
export const copyDocument = (id, data) => api.post(`/documents/${id}/copy`, data);
export const downloadFolder = (folderId) => api.get(`/folders/${folderId}/download`, { responseType: 'blob' });
export const getSelectedDocuments = (caseId, folderIds) =>
  api.post(`/cases/${caseId}/selected-documents`, { selected_folder_ids: folderIds });

export const passEditingRights = (sessionId, data) =>
  api.post(`/depositions/${sessionId}/pass-editing`, data);

export const revokeEditingRights = (sessionId) =>
  api.post(`/depositions/${sessionId}/revoke-editing`);

// === ONLYOFFICE: CORRECTED TO POST ===
export const getDocumentEditorConfig = (docId, identity = {}) => {
  const params = {};

  if (identity.userId) {
    params.user_id = identity.userId;
  }
  if (identity.displayName) {
    params.display_name = identity.displayName;
  }
  if (identity.role) {
    params.role = identity.role;
  }
  if (typeof identity.canEdit !== 'undefined') {
    params.can_edit = identity.canEdit ? 1 : 0;
  }

  return api.get(`/onlyoffice/editor/${docId}`, { params });
};
export const uploadDocumentSignature = (documentId, signature, command = 'add') =>
  api.post(`/onlyoffice/document/${documentId}/signature-upload`, { signature, command });

export const getZoomConnectUrl = () => api.get('/zoom/oauth/connect-url');

export const getZoomOAuthStatus = () => api.get('/zoom/oauth/status');

export const connectZoomOAuth = async () => {
  const res = await getZoomConnectUrl();
  const url = res?.data?.url;

  if (!url) {
    throw new Error('Zoom connect URL not returned from backend');
  }

  window.location.href = url;
};


export const getSessionZoomDetails = (sessionId) =>
  api.get(`/sessions/${sessionId}/zoom`);

export const createSessionZoomMeeting = (sessionId) =>
  api.post(`/sessions/${sessionId}/zoom/create`);



export const generateSSOToken = async () => {
  const token = localStorage.getItem("sanctum_token"); // use correct key

  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/webmanager/sso-token`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to generate SSO token");
  }

  return await response.json(); // { token: "JWT..." }
};


export default api;
